import React from 'react';

interface ModelInfo {
  name: string;
  url: string;
  size: number;
  checksum?: string;
  required: boolean;
}

interface DownloadProgress {
  modelName: string;
  downloaded: number;
  total: number;
  percentage: number;
  status: 'pending' | 'downloading' | 'completed' | 'failed';
  error?: string;
}

class ModelDownloadService {
  private models: ModelInfo[] = [
    {
      name: 'gemma-3n-e2b-q4',
      url: '/models/gemma-3n-e2b/model_quantized.onnx',
      size: 500 * 1024 * 1024, // 500MB estimate
      required: true,
    },
    {
      name: 'gemma-3n-tokenizer',
      url: '/models/gemma-3n-e2b/tokenizer.json',
      size: 2 * 1024 * 1024, // 2MB
      required: true,
    },
    {
      name: 'tesseract-eng',
      url: '/models/tesseract/eng.traineddata',
      size: 50 * 1024 * 1024, // 50MB
      required: false,
    },
  ];
  
  private downloadProgress: Map<string, DownloadProgress> = new Map();
  private listeners: ((progress: DownloadProgress[]) => void)[] = [];
  
  constructor() {
    // Listen for service worker messages
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('message', (event) => {
        if (event.data.type === 'MODEL_DOWNLOAD_PROGRESS') {
          this.handleDownloadProgress(event.data);
        } else if (event.data.type === 'MODELS_READY') {
          this.handleModelsReady(event.data.models);
        } else if (event.data.type === 'MODEL_DOWNLOAD_FAILED') {
          this.handleDownloadFailed(event.data.error);
        }
      });
    }
  }
  
  onProgress(callback: (progress: DownloadProgress[]) => void): () => void {
    this.listeners.push(callback);
    
    // Send current progress immediately
    callback(this.getAllProgress());
    
    // Return unsubscribe function
    return () => {
      this.listeners = this.listeners.filter(l => l !== callback);
    };
  }
  
  private notifyListeners(): void {
    const progress = this.getAllProgress();
    this.listeners.forEach(listener => listener(progress));
  }
  
  private getAllProgress(): DownloadProgress[] {
    return Array.from(this.downloadProgress.values());
  }
  
  async checkModelsStatus(): Promise<{
    allDownloaded: boolean;
    requiredDownloaded: boolean;
    models: Array<ModelInfo & { downloaded: boolean; cached: boolean }>;
  }> {
    const status = await Promise.all(
      this.models.map(async (model) => {
        const cached = await this.isModelCached(model.url);
        const downloaded = cached || (await this.isModelDownloaded(model.url));
        
        return {
          ...model,
          downloaded,
          cached,
        };
      })
    );
    
    const allDownloaded = status.every(m => m.downloaded);
    const requiredDownloaded = status.filter(m => m.required).every(m => m.downloaded);
    
    return {
      allDownloaded,
      requiredDownloaded,
      models: status,
    };
  }
  
  private async isModelCached(url: string): Promise<boolean> {
    if (!('caches' in window)) return false;
    
    try {
      const cache = await caches.open('exan-models-v1');
      const response = await cache.match(url);
      return response !== undefined;
    } catch {
      return false;
    }
  }
  
  private async isModelDownloaded(url: string): Promise<boolean> {
    try {
      const response = await fetch(url, { method: 'HEAD' });
      return response.ok;
    } catch {
      return false;
    }
  }
  
  async downloadModels(options?: {
    onlyRequired?: boolean;
    forceRedownload?: boolean;
  }): Promise<void> {
    const status = await this.checkModelsStatus();
    
    if (!options?.forceRedownload && 
        (options?.onlyRequired ? status.requiredDownloaded : status.allDownloaded)) {
      console.log('Models already downloaded');
      return;
    }
    
    const modelsToDownload = this.models.filter(model => {
      if (options?.onlyRequired && !model.required) return false;
      if (options?.forceRedownload) return true;
      const modelStatus = status.models.find(m => m.url === model.url);
      return !modelStatus?.downloaded;
    });
    
    if (modelsToDownload.length === 0) {
      console.log('No models to download');
      return;
    }
    
    // Initialize progress tracking
    modelsToDownload.forEach(model => {
      this.downloadProgress.set(model.name, {
        modelName: model.name,
        downloaded: 0,
        total: model.size,
        percentage: 0,
        status: 'pending',
      });
    });
    
    this.notifyListeners();
    
    // Check if we can use background fetch
    if ('serviceWorker' in navigator && 'BackgroundFetchManager' in window) {
      await this.downloadWithBackgroundFetch(modelsToDownload);
    } else {
      // Fallback to regular fetch
      await this.downloadWithFetch(modelsToDownload);
    }
  }
  
  private async downloadWithBackgroundFetch(models: ModelInfo[]): Promise<void> {
    if (!navigator.serviceWorker.controller) {
      throw new Error('Service worker not active');
    }
    
    // Send message to service worker to start background fetch
    navigator.serviceWorker.controller.postMessage({
      type: 'DOWNLOAD_MODELS',
      models: models.map(m => m.url),
    });
    
    // Progress will be handled via service worker messages
  }
  
  private async downloadWithFetch(models: ModelInfo[]): Promise<void> {
    for (const model of models) {
      try {
        await this.downloadSingleModel(model);
      } catch (error) {
        console.error(`Failed to download ${model.name}:`, error);
        
        const progress = this.downloadProgress.get(model.name)!;
        progress.status = 'failed';
        progress.error = error instanceof Error ? error.message : 'Download failed';
        this.notifyListeners();
        
        if (model.required) {
          throw error; // Stop if required model fails
        }
      }
    }
  }
  
  private async downloadSingleModel(model: ModelInfo): Promise<void> {
    const progress = this.downloadProgress.get(model.name)!;
    progress.status = 'downloading';
    this.notifyListeners();
    
    const response = await fetch(model.url);
    
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }
    
    const contentLength = Number(response.headers.get('content-length')) || model.size;
    progress.total = contentLength;
    
    if (!response.body) {
      throw new Error('Response body is null');
    }
    
    // Read the response with progress tracking
    const reader = response.body.getReader();
    const chunks: Uint8Array[] = [];
    let receivedLength = 0;
    
    while (true) {
      const { done, value } = await reader.read();
      
      if (done) break;
      
      chunks.push(value);
      receivedLength += value.length;
      
      progress.downloaded = receivedLength;
      progress.percentage = (receivedLength / contentLength) * 100;
      this.notifyListeners();
    }
    
    // Combine chunks
    const blob = new Blob(chunks);
    
    // Verify checksum if provided
    if (model.checksum) {
      const arrayBuffer = await blob.arrayBuffer();
      const checksum = await this.calculateChecksum(arrayBuffer);
      
      if (checksum !== model.checksum) {
        throw new Error('Checksum verification failed');
      }
    }
    
    // Cache the model
    if ('caches' in window) {
      const cache = await caches.open('exan-models-v1');
      await cache.put(model.url, new Response(blob));
    }
    
    progress.status = 'completed';
    progress.percentage = 100;
    this.notifyListeners();
  }
  
  private async calculateChecksum(data: ArrayBuffer): Promise<string> {
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  }
  
  private handleDownloadProgress(data: {
    percent: number;
    downloaded: number;
    downloadTotal: number;
  }): void {
    // Update all downloading models with the same progress
    // (Background fetch doesn't provide per-file progress)
    this.downloadProgress.forEach((progress) => {
      if (progress.status === 'downloading' || progress.status === 'pending') {
        progress.downloaded = (data.percent / 100) * progress.total;
        progress.percentage = data.percent;
        progress.status = 'downloading';
      }
    });
    
    this.notifyListeners();
  }
  
  private handleModelsReady(modelUrls: string[]): void {
    // Mark completed models
    this.models.forEach(model => {
      if (modelUrls.includes(model.url)) {
        const progress = this.downloadProgress.get(model.name);
        if (progress) {
          progress.status = 'completed';
          progress.percentage = 100;
          progress.downloaded = progress.total;
        }
      }
    });
    
    this.notifyListeners();
  }
  
  private handleDownloadFailed(error: string): void {
    // Mark all pending/downloading as failed
    this.downloadProgress.forEach((progress) => {
      if (progress.status === 'downloading' || progress.status === 'pending') {
        progress.status = 'failed';
        progress.error = error;
      }
    });
    
    this.notifyListeners();
  }
  
  async clearModelCache(): Promise<void> {
    if ('caches' in window) {
      await caches.delete('exan-models-v1');
    }
    
    // Clear progress
    this.downloadProgress.clear();
    this.notifyListeners();
  }
  
  getTotalDownloadSize(): number {
    return this.models.reduce((sum, model) => sum + model.size, 0);
  }
  
  getRequiredDownloadSize(): number {
    return this.models
      .filter(m => m.required)
      .reduce((sum, model) => sum + model.size, 0);
  }
}

// Export singleton instance
export const modelDownload = new ModelDownloadService();

// React hook for download progress
export function useModelDownload() {
  const [progress, setProgress] = React.useState<DownloadProgress[]>([]);
  const [isDownloading, setIsDownloading] = React.useState(false);
  
  React.useEffect(() => {
    const unsubscribe = modelDownload.onProgress((newProgress) => {
      setProgress(newProgress);
      setIsDownloading(newProgress.some(p => p.status === 'downloading'));
    });
    
    // Check initial status
    modelDownload.checkModelsStatus().then(status => {
      if (!status.requiredDownloaded) {
        // Auto-download required models
        modelDownload.downloadModels({ onlyRequired: true })
          .catch(console.error);
      }
    });
    
    return unsubscribe;
  }, []);
  
  const downloadAll = React.useCallback(async () => {
    try {
      await modelDownload.downloadModels({ onlyRequired: false });
    } catch (error) {
      console.error('Download failed:', error);
    }
  }, []);
  
  const retry = React.useCallback(async () => {
    try {
      await modelDownload.downloadModels({ forceRedownload: true });
    } catch (error) {
      console.error('Retry failed:', error);
    }
  }, []);
  
  return {
    progress,
    isDownloading,
    downloadAll,
    retry,
    totalSize: modelDownload.getTotalDownloadSize(),
    requiredSize: modelDownload.getRequiredDownloadSize(),
  };
}